import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, MotionValue } from "framer-motion";

const MarqueeRow = ({
  items,
  direction = "left",
  speed = 1
}: {
  items: { title: string; subtitle: string; bg: string; id: number }[];
  direction?: "left" | "right";
  speed?: number;
}) => {
  // Duplicate items to ensure infinite feel before reset if needed, 
  // though purely CSS/framer infinite loops are often smoother. 
  // Here we use scroll-linked parallax.

  // We'll repeat the items 3 times to cover width
  const repeatedItems = [...items, ...items, ...items];

  return (
    <div className="flex gap-8 py-4 whitespace-nowrap">
      {repeatedItems.map((card, i) => (
        <div
          key={`${card.id}-${i}`}
          className={`relative h-[300px] w-[25vw] min-w-[300px] shrink-0 overflow-hidden rounded-3xl ${card.bg} border border-black/5 shadow-sm transition-transform hover:scale-95 duration-500`}
        >
          <div className="absolute inset-0 flex flex-col justify-between p-8 text-black/80">
            <div>
              <p className="text-sm font-bold opacity-60 uppercase tracking-widest mb-2">Step 0{card.id}</p>
              <h3 className="text-4xl font-black uppercase leading-none">{card.title}</h3>
            </div>
            <p className="text-xl font-medium">{card.subtitle}</p>
          </div>
        </div>
      ))}
    </div>
  );
};


const HorizontalScrollSection = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"]
  });

  // Create smooth varying speeds/directions for rows
  const xLeft = useTransform(scrollYProgress, [0, 1], ["0%", "-50%"]);
  const xRight = useTransform(scrollYProgress, [0, 1], ["-50%", "0%"]);

  const cards1 = [
    { title: "Connect", subtitle: "Instant Matching", id: 1, bg: "bg-white" },
    { title: "Book", subtitle: "Secure Fee", id: 2, bg: "bg-blue-400" },
    { title: "Track", subtitle: "Live Updates", id: 3, bg: "bg-purple-400" },
    { title: "Deliver", subtitle: "Proof of Delivery", id: 4, bg: "bg-emerald-400" },
  ];

  const cards2 = [
    { title: "AI Route", subtitle: "Optimized Path", id: 5, bg: "bg-indigo-400" },
    { title: "Instant Pay", subtitle: "Zero Delays", id: 6, bg: "bg-pink-400" },
    { title: "Verified", subtitle: "Trusted Fleet", id: 7, bg: "bg-orange-400" },
    { title: "Global", subtitle: "Borderless", id: 8, bg: "bg-cyan-400" },
  ];

  return (
    <section className="py-24 bg-background overflow-hidden relative transition-colors duration-500">
      <div className="container mx-auto px-4 mb-16 text-center">
        <h2 className="text-4xl md:text-8xl font-black text-foreground uppercase leading-none tracking-tighter">
          The Process <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600">
            Unfolded
          </span>
        </h2>
      </div>

      <div ref={targetRef} className="flex flex-col gap-8 -rotate-2 scale-105 origin-center">
        {/* Row 1: Left to Right (visually, so starts left goes right? Or content moves left?)
                Typically "marquee" moves continuously. Here we bind to scroll. 
                xLeft moves 0 -> -50% (Content moves Left)
                xRight moves -50% -> 0% (Content moves Right)
            */}

        <motion.div style={{ x: xLeft }} className="flex w-max">
          <MarqueeRow items={cards1} direction="left" />
        </motion.div>

        <motion.div style={{ x: xRight }} className="flex w-max">
          <MarqueeRow items={cards2} direction="right" />
        </motion.div>

        <motion.div style={{ x: xLeft }} className="flex w-max">
          {/* Repeat cards1 with different colors/order if desired, or same */}
          <MarqueeRow items={[...cards1].reverse()} direction="left" />
        </motion.div>
      </div>

      {/* Vignette for depth - adaptive */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />
    </section>
  );
};

export default HorizontalScrollSection;
